import { useEffect, useRef, useState } from 'react'
import ChatMessageBubbleContainer from '../components/chat/ChatMessageBubbleContainer'
import { useActivities } from '../hooks/activity/useActivities'
import { useActivityLogs } from '../hooks/activity/useActivityLogs'
import { isFirstInChain, isLastInChain } from '../hooks/chat/chatUtils'
import { useMessageSubscription } from '../hooks/chat/useMessageSubscription'
import { useSendMessage } from '../hooks/chat/useSendMessage'
import { useChallengeAccess } from '../hooks/membership/useChallengeAccess'
import { useNotifications } from '../hooks/notifications/useNotifications'
import { useTeam } from '../hooks/team/useTeam'
import { useUser } from '../hooks/user/useUser'
import Message from '../model/Message'
import ChatHeader from '../ui/chat/ChatHeader'
import ChatMessageInput from '../ui/chat/ChatMessageInput'
import styles from '../ui/chat/ChatScreen.module.css'
import AppShell from '../ui/layout/AppShell'
import { cx } from '../ui/util/cx'

export default function ChatScreen() {
  const [replyTo, setReplyTo] = useState<Message | undefined>()
  const { data: user } = useUser()
  const { teamID } = useChallengeAccess()
  const { data: team } = useTeam(teamID ?? '')
  const { messages, isLoading, isError, addMessage } = useMessageSubscription(teamID)
  const { data: activityLogs } = useActivityLogs()
  const { data: activities } = useActivities()
  const { mutateAsync: sendMessageMutation, isPending: isSendingMessage } = useSendMessage()
  const { readChatNotifications } = useNotifications()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const activityLogsMap = new Map(activityLogs?.map((log) => [log.id, log]) ?? [])
  const activitiesMap = new Map(activities?.map((activity) => [activity.id, activity]) ?? [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages])

  useEffect(() => {
    readChatNotifications()
  }, [readChatNotifications])

  async function handleSendMessage(
    messageText: string,
    replyToId?: string,
    images?: Parameters<typeof sendMessageMutation>[0]['images'],
    files?: File[],
  ) {
    if (messageText.trim() === '' && (!images || images.length === 0)) return
    const message = await sendMessageMutation({ messageText, replyToId, images, files })
    addMessage(message)
    setReplyTo(undefined)
  }

  function handleScrollToMessage(messageId: string) {
    const element = messageRefs.current[messageId]
    if (!element) return
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    element.classList.add(styles.highlight)
    setTimeout(() => element.classList.remove(styles.highlight), 1000)
  }

  const header = (
    <ChatHeader title={team?.name ?? 'Chat'} memberCount={team ? team.members.length : undefined} />
  )

  if (!user) {
    return (
      <div className={styles.screen}>
        <ChatHeader title="Chat" />
        <div className={styles.thread}>
          <AppShell>
            <p className={styles.status}>Loading…</p>
          </AppShell>
        </div>
      </div>
    )
  }

  if (!teamID) {
    return (
      <div className={styles.screen}>
        {header}
        <div className={styles.thread}>
          <AppShell>
            <p className={styles.status}>Join a team to chat.</p>
          </AppShell>
        </div>
      </div>
    )
  }

  const threadStatus = threadStatusCopy(isLoading, isError, messages.length)

  return (
    <div className={styles.screen}>
      {header}
      <div className={styles.thread}>
        <AppShell
          footer={
            <ChatMessageInput
              onSend={handleSendMessage}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(undefined)}
              disabled={isSendingMessage}
            />
          }
        >
          <div className={styles.messages}>
            {threadStatus ? (
              <p className={styles.status}>{threadStatus}</p>
            ) : (
              messages.map((message, index) => {
                const isOwnMessage = message.user.id === user.id
                const isActivity = !!message.activityLogId
                const last = isLastInChain(messages, index)
                const first = isFirstInChain(messages, index)

                return (
                  <div
                    key={message.id}
                    ref={(el) => {
                      messageRefs.current[message.id] = el
                    }}
                    className={cx(
                      styles.row,
                      isActivity ? styles.rowActivity : isOwnMessage ? styles.rowOwn : styles.rowOther,
                      last && styles.rowGap,
                    )}
                  >
                    <ChatMessageBubbleContainer
                      message={message}
                      messages={messages}
                      isOwnMessage={isOwnMessage}
                      isFirstInChain={first}
                      isLastInChain={last}
                      onReply={setReplyTo}
                      onScrollToMessage={handleScrollToMessage}
                      activityLogsMap={activityLogsMap}
                      activitiesMap={activitiesMap}
                    />
                  </div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </AppShell>
      </div>
    </div>
  )
}

function threadStatusCopy(isLoading: boolean, isError: boolean, messageCount: number) {
  if (messageCount > 0) return null
  if (isLoading) return 'Loading…'
  if (isError) return 'Could not load messages.'
  return 'No messages yet.'
}
